/**
 * Algolia Recommend Section Customization
 * This script modifies the Algolia Recommend cards after they're rendered
 * to match the site's product card design
 */

(function() {
  'use strict';

  const ALGOLIA_SECTION_SELECTOR = '.algolia-recommend-block';
  const CARD_SELECTOR = '.card-wrapper';
  const MAX_WAIT_TIME = 10000; // 10 seconds max wait
  const CHECK_INTERVAL = 100; // Check every 100ms

  /**
   * Wait for Algolia cards to be rendered in the DOM
   */
  function waitForAlgoliaCards(callback) {
    const startTime = Date.now();
    
    const checkForCards = () => {
      const section = document.querySelector(ALGOLIA_SECTION_SELECTOR);

      if (!section) {
        if (Date.now() - startTime < MAX_WAIT_TIME) {
          setTimeout(checkForCards, CHECK_INTERVAL);
        }
        return;
      }
      
      const cards = section.querySelectorAll(CARD_SELECTOR);
      
      if (cards.length > 0) {
        callback(section, cards);
      } else if (Date.now() - startTime < MAX_WAIT_TIME) {
        setTimeout(checkForCards, CHECK_INTERVAL);
      }
    };

    checkForCards();
  }

  /**
   * Split product title into product name and variant
   * Using the last " - " as the delimiter
   */
  function splitProductTitle(title) {
    const lastDashIndex = title.lastIndexOf(' - ');
    
    if (lastDashIndex === -1) {
      return { productName: title, variantName: '' };
    }

    return {
      productName: title.substring(0, lastDashIndex).trim(),
      variantName: title.substring(lastDashIndex + 3).trim()
    };
  }

  /**
   * Create Quick Add button element
   */
  function createQuickAddButton(productUrl) {
    const button = document.createElement('button');
    button.className = 'button button--full-width quick-add-button';
    button.textContent = 'Quick Add';
    button.type = 'button';
    
    // Add click handler to navigate to product page
    button.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      window.location.href = productUrl;
    });
    
    return button;
  }

  /**
   * Remove 200x (1x) images from srcset and update src to use 600x (3x) by default
   */
  function updateImageResolution(card) {
    const images = card.querySelectorAll('img[srcset]');
    
    images.forEach(img => {
      // Update src to use 600x (3x) image instead of 200x (1x)
      const currentSrc = img.getAttribute('src');
      if (currentSrc && currentSrc.includes('_200x')) {
        const newSrc = currentSrc.replace(/_200x\./g, '_600x.');
        img.setAttribute('src', newSrc);
      }
      
      // Remove 200x (1x) entry from srcset
      const currentSrcset = img.getAttribute('srcset');
      if (currentSrcset) {
        // Remove the 200x 1x entry from srcset
        const newSrcset = currentSrcset
          .split(',')
          .map(entry => entry.trim())
          .filter(entry => !entry.includes('_200x'))
          .join(', ');
        
        if (newSrcset) {
          img.setAttribute('srcset', newSrcset);
        }
      }
    });
  }

  /**
   * Transform a single card to match the site's design
   */
  function transformCard(card) {
    try {
      // Skip if already transformed
      if (card.getAttribute('data-card-transformed') === 'true') {
        return;
      }
      
      // Find the card heading/title links
      const titleLinks = card.querySelectorAll('.card__heading a');
      
      if (titleLinks.length === 0) {
        return;
      }

      // Get the product title and URL from the first link
      const firstTitleLink = titleLinks[0];
      const fullTitle = firstTitleLink.textContent.trim();
      let productUrl = firstTitleLink.getAttribute('href') || firstTitleLink.href;
      
      if (!fullTitle) {
        return;
      }
      
      // If no URL, try to find it from another link in the card
      if (!productUrl) {
        const cardLinks = card.querySelectorAll('a[href*="/products/"]');
        if (cardLinks.length > 0) {
          productUrl = cardLinks[0].getAttribute('href') || cardLinks[0].href;
        }
      }
      
      if (!productUrl) {
        return;
      }

      // Update image resolution to use 3x (600x) images
      updateImageResolution(card);

      // Split title into product name and variant
      const { productName, variantName } = splitProductTitle(fullTitle);

      // Update all title links with split title
      titleLinks.forEach(link => {
        link.innerHTML = '';
        
        // Add product name
        const productNameSpan = document.createElement('span');
        productNameSpan.className = 'product-name';
        productNameSpan.textContent = productName;
        link.appendChild(productNameSpan);
        
        // Add variant name on new line if it exists
        if (variantName) {
          const variantNameSpan = document.createElement('span');
          variantNameSpan.className = 'variant-name';
          variantNameSpan.textContent = variantName;
          link.appendChild(variantNameSpan);
        }
      });

      // Wrap the entire card with an anchor tag to make it clickable
      if (!card.closest('a.card-wrapper-link')) {
        // Create wrapper anchor
        const cardLink = document.createElement('a');
        cardLink.href = productUrl;
        cardLink.className = 'card-wrapper-link';
        cardLink.style.display = 'block';
        cardLink.style.textDecoration = 'none';
        cardLink.style.color = 'inherit';
        
        // Wrap the card with the anchor
        const parent = card.parentNode;
        parent.insertBefore(cardLink, card);
        cardLink.appendChild(card);
        
        // Prevent nested links from propagating clicks
        const nestedLinks = card.querySelectorAll('a');
        nestedLinks.forEach(link => {
          link.addEventListener('click', (e) => {
            e.stopPropagation();
          });
        });
      }

      // Mark card as transformed
      card.setAttribute('data-card-transformed', 'true');

    } catch (error) {
      console.error('Error transforming Algolia card:', error);
    }
  }

  /**
   * Transform all cards in the section
   */
  function transformAllCards(section, cards) {
    cards.forEach(card => {
      transformCard(card);
    });

    // Mark section as transformed to prevent re-processing
    section.setAttribute('data-algolia-transformed', 'true');
  }

  /**
   * Initialize the script
   */
  function init() {
    // Wait for Algolia to render cards
    waitForAlgoliaCards((section, cards) => {
      // Check if already transformed
      if (section.getAttribute('data-algolia-transformed') === 'true') {
        return;
      }
      
      transformAllCards(section, cards);
    });

    // Also observe for dynamic changes (e.g., if Algolia re-renders)
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.addedNodes.length > 0) {
          const section = document.querySelector(ALGOLIA_SECTION_SELECTOR);
          if (section && section.getAttribute('data-algolia-transformed') !== 'true') {
            const cards = section.querySelectorAll(CARD_SELECTOR);
            if (cards.length > 0) {
              transformAllCards(section, cards);
            }
          }
        }
      });
    });

    // Start observing the document for changes
    if (document.body) {
      observer.observe(document.body, {
        childList: true,
        subtree: true
      });
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

